import React from "react";
import { Dialog } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./index.module.css";

/**
 * CustomPopup — MUI v5 Dialog base with custom styling.
 *
 * Props:
 *  open                boolean     required — controls visibility
 *  onClose             function    called on backdrop click, Escape key, or × button
 *  title               string | ReactNode  — centered heading
 *  subtitle            string | ReactNode  — centered grey subheading below title
 *  children            ReactNode   — main scrollable content area
 *  footer              ReactNode   — rendered below content, outside scroll (e.g. "Go back" button)
 *  showCloseButton     boolean     (default: true)  — shows × at top-right
 *  disableBackdropClick boolean    (default: false) — prevents closing on backdrop click
 *
 *  width               string | number  — exact Paper width  e.g. "480px" or 480
 *  maxWidth            string           (default: "sm") — MUI breakpoint: "xs"|"sm"|"md"|"lg"|false
 *  height              string | number  — exact Paper height e.g. "600px" — content area scrolls
 *  maxHeight           string | number  — cap scrollable area height
 *  fullWidth           boolean     (default: true)
 *
 *  padding             string      (default: "24px 16px") — content area padding
 *  mobilePadding       string      — content area padding at <=480px (overrides `padding`)
 *  className           string      — extra class applied to the Paper element
 */
const CustomPopup = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  showCloseButton = true,
  disableBackdropClick = false,
  width,
  maxWidth = "sm",
  height,
  maxHeight,
  fullWidth = true,
  padding = "24px 16px",
  mobilePadding,
  className,
}) => {
  const handleClose = (event, reason) => {
    if (disableBackdropClick && reason === "backdropClick") return;
    onClose?.(event, reason);
  };

  const paperStyle = {
    ...(width !== undefined && {
      width: typeof width === "number" ? `${width}px` : width,
    }),
    ...(height !== undefined && {
      height: typeof height === "number" ? `${height}px` : height,
    }),
    ...(maxHeight !== undefined && {
      maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
    }),
  };

  const hasHeader = Boolean(title || subtitle);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        className: [styles.paper, className].filter(Boolean).join(" "),
        style: paperStyle,
      }}
    >
      {/* ── Close button ── */}
      {showCloseButton && (
        <button
          className={styles.closeButton}
          onClick={() => onClose?.()}
          aria-label="Close"
          type="button"
        >
          <CloseIcon style={{ width: 20, height: 20 }} />
        </button>
      )}

      {/* ── Header ── */}
      {hasHeader && (
        <div className={styles.header}>
          {title && <h2 className={styles.customTitle}>{title}</h2>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}

      {/* ── Content ── */}
      <div
        className={styles.content}
        style={{
          "--popup-padding": padding,
          ...(mobilePadding && { "--popup-padding-mobile": mobilePadding }),
        }}
      >
        {children}
      </div>

      {/* ── Footer ── */}
      {footer && <div className={styles.footer}>{footer}</div>}
    </Dialog>
  );
};

export default CustomPopup;
